import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import "./Style.css";

interface NavigationCalendarProps {
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const NavigationCalendar: React.FC<NavigationCalendarProps> = ({
  selectedDate,
  onSelectDate,
}) => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));

  const isMobile = screenWidth < 780;

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);

    const startDate = new Date(firstDay);
    const dayOfWeek = (firstDay.getDay() + 6) % 7;
    startDate.setDate(firstDay.getDate() - dayOfWeek);

    const days: Date[] = [];
    const current = new Date(startDate);
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const formatMonthYear = (date: Date) =>
    date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + (direction === 'next' ? 1 : -1));
    setCurrentMonth(newMonth);
  };

  const isToday = (date: Date) => date.toDateString() === new Date().toDateString();
  const isSelected = (date: Date) => date.toDateString() === selectedDate.toDateString();
  const isCurrentMonth = (date: Date) => date.getMonth() === currentMonth.getMonth();

  const handleDateClick = (date: Date) => {
    onSelectDate(date);
    if (date.getMonth() !== currentMonth.getMonth()) {
      setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    }
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className={cn(
      "bg-background rounded-md p-4 border border-border min-w-[300px] max-w-[350px]",
      isMobile && "min-w-[280px] mx-5"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 h-8">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 p-0"
          onClick={() => navigateMonth('prev')}
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-base font-semibold text-foreground">
          {formatMonthYear(currentMonth)}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 p-0"
          onClick={() => navigateMonth('next')}
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-0.5 mb-2">
        {WEEKDAYS.map(day => (
          <div key={day} className="flex items-center justify-center h-8 text-xs font-semibold text-muted-foreground">
            {day}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((date, index) => {
          const today = isToday(date);
          const selected = isSelected(date);
          const inMonth = isCurrentMonth(date);

          return (
            <Button
              key={index}
              variant={today ? "default" : selected ? "secondary" : "ghost"}
              size="icon"
              className={cn(
                "h-8 w-8 p-0 text-sm rounded-sm",
                today && "bg-primary text-primary-foreground hover:bg-primary/90",
                selected && !today && "bg-primary/20 text-primary font-semibold border-2 border-primary",
                !inMonth && "text-muted-foreground opacity-50"
              )}
              onClick={() => handleDateClick(date)}
            >
              {date.getDate()}
            </Button>
          );
        })}
      </div>

      {/* Go to Today */}
      <div className="flex justify-end mt-3">
        <Button
          size="sm"
          className="text-xs"
          onClick={() => {
            const today = new Date();
            onSelectDate(today);
            setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
          }}
          disabled={isToday(selectedDate)}
        >
          Go to today
        </Button>
      </div>
    </div>
  );
};

export default NavigationCalendar;
